/**
 * Freezing the trimester, and importing the export that was taken at the time.
 *
 * The two live together because they answer one question — what is this round
 * measured against — by two routes. Freezing reads Airtable now; importing is
 * for a round that opened before anyone thought to freeze it, where a CSV taken
 * on the day is the only copy of that table there will ever be (Airtable keeps
 * no field history).
 *
 * Lifted out of HiringDemandTab, which was over the 500-line ceiling: these are
 * two closures over `run` and nothing else in that file reads them.
 */
import type { DemandSnapshot } from '../../../types/taDemand';
import { freezeDemandBaseline, importDemandBaseline } from '../../../services/taDemandApi';
import { BaselineBar } from './BaselineBar';

export function BaselineActions({
  period,
  snapshots,
  busy,
  run,
  setNotice,
}: {
  period: string;
  snapshots: DemandSnapshot[];
  busy: boolean;
  run: (key: string, fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;
  setNotice: (s: string) => void;
}) {
  return (
    <BaselineBar
      snapshots={snapshots}
      busy={busy}
      onFreeze={(kind) =>
        void run(
          'freeze-baseline',
          () => freezeDemandBaseline(period, { kind }),
          kind === 'baseline'
            ? 'Baseline frozen. This is what the trimester will be measured against.'
            : 'Snapshot taken.',
        )
      }
      onImport={(file) =>
        run('freeze-baseline', async () => {
          const { snapshot, unmapped } = await importDemandBaseline(period, {
            ...file,
            kind: 'baseline',
          });
          setNotice(
            `Frozen ${snapshot.rowCount} roles as the baseline for ${snapshot.takenAt.slice(0, 10)}.` +
              // Naming what did not map is the difference between a baseline and
              // a baseline with silent holes in it.
              (unmapped.length ? ` Columns not recognised: ${unmapped.join(', ')}.` : ''),
          );
        })
      }
    />
  );
}
